import { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import type { DateRange } from '../../../types/bi';
import styles from './DateRangePicker.module.css';

const PRESETS: { label: string; value: string }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'This month', value: 'this_month' },
  { label: 'Last month', value: 'last_month' },
  { label: 'This quarter', value: 'this_quarter' },
  { label: 'Last quarter', value: 'last_quarter' },
  { label: 'This year', value: 'this_year' },
  { label: 'Last year', value: 'last_year' },
  { label: 'All time', value: 'all_time' },
];

interface DateRangePickerProps {
  value: DateRange | null;
  onChange: (range: DateRange | null) => void;
}

export default function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'relative' | 'absolute'>(value?.type || 'relative');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Sync absolute inputs when value changes externally
  useEffect(() => {
    if (value?.type === 'absolute' && typeof value.value === 'object') {
      setFrom(value.value.from);
      setTo(value.value.to);
    }
  }, [value]);

  // Click-away close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectPreset = useCallback(
    (preset: string) => {
      if (preset === 'all_time') {
        onChange(null);
      } else {
        onChange({ type: 'relative', value: preset });
      }
      setOpen(false);
    },
    [onChange],
  );

  const applyAbsolute = useCallback(() => {
    if (from && to) {
      onChange({ type: 'absolute', value: { from, to } });
      setOpen(false);
    }
  }, [from, to, onChange]);

  const displayLabel = (() => {
    if (!value) return 'All time';
    if (value.type === 'relative') {
      const found = PRESETS.find((p) => p.value === value.value);
      return found?.label || String(value.value);
    }
    if (typeof value.value === 'object') {
      return `${value.value.from} → ${value.value.to}`;
    }
    return 'Custom';
  })();

  return (
    <div className={styles.wrapper} ref={ref}>
      <button className={styles.trigger} onClick={() => setOpen(!open)}>
        <FontAwesomeIcon icon={faCalendar} />
        <span>{displayLabel}</span>
        <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${mode === 'relative' ? styles.active : ''}`}
              onClick={() => setMode('relative')}
            >
              Presets
            </button>
            <button
              className={`${styles.tab} ${mode === 'absolute' ? styles.active : ''}`}
              onClick={() => setMode('absolute')}
            >
              Custom
            </button>
          </div>

          {mode === 'relative' ? (
            <div className={styles.presetList}>
              {PRESETS.map((p) => {
                const selected =
                  (p.value === 'all_time' && !value) ||
                  (value?.type === 'relative' && value.value === p.value);
                return (
                  <button
                    key={p.value}
                    className={`${styles.presetBtn} ${selected ? styles.selected : ''}`}
                    onClick={() => selectPreset(p.value)}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={styles.absolutePanel}>
              <label className={styles.label}>
                From
                <input
                  type="date"
                  className={styles.dateInput}
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className={styles.label}>
                To
                <input
                  type="date"
                  className={styles.dateInput}
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
              <button className={styles.applyBtn} onClick={applyAbsolute} disabled={!from || !to}>
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
